import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FORMS_API_URL } from '../utils/formSessionApi';
import { getContractApiUrl } from '../utils/apiUrls';
import PhoneNumberInput from './PhoneNumberInput';

const BACKEND_URL = getContractApiUrl();

const AGENT_INFO_FIELDS = [
  { key: 'name', label: 'Agent name', placeholder: 'e.g. Jane Smith' },
  { key: 'license', label: 'License number', placeholder: 'e.g. 456789, 654321-SA, or ABC-12345' },
  { key: 'email', label: 'Email', placeholder: 'e.g. agent@example.com' },
  { key: 'phone', label: 'Phone', placeholder: 'e.g. 405-555-1234', inputType: 'phone' },
  { key: 'brokerage', label: 'Brokerage name', placeholder: 'e.g. EXP Realty' },
  { key: 'brokerage_license', label: 'Brokerage license number', placeholder: 'e.g. 123456 or ABC-12345' },
  { key: 'brokerage_address', label: 'Brokerage address', placeholder: 'e.g. 123 Main St, Oklahoma City, OK' },
  { key: 'brokerage_phone', label: 'Brokerage phone', placeholder: 'e.g. 405-555-0100', inputType: 'phone' },
];

const fieldKeyForSide = (side, key) => {
  const prefix = side === 'buyer' ? 'buyer_agent' : 'sellers_agent';
  if (key === 'license') return `${prefix}_license`;
  return `${prefix}_${key}`;
};

const contactToFieldUpdates = (side, contact) => {
  const updates = {};
  AGENT_INFO_FIELDS.forEach(({ key }) => {
    updates[fieldKeyForSide(side, key)] = contact[key] || '';
  });
  return updates;
};

async function fetchOpposingAgentContacts({
  side,
  query,
  formSessionToken,
  userId,
}) {
  const params = new URLSearchParams({ side, q: query || '' });
  const url = formSessionToken
    ? `${FORMS_API_URL}/sessions/${formSessionToken}/opposing-agent-contacts?${params}`
    : `${BACKEND_URL}/users/${userId}/opposing-agent-contacts?${params}`;

  const res = await fetch(url);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || 'Failed to load agent contacts');
  }
  return data.contacts || [];
}

const OpposingAgentInfoFields = ({
  side,
  contractData,
  setContractData,
  formSessionToken = null,
  userProfile = null,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const nameKey = fieldKeyForSide(side, 'name');
  const nameValue = contractData[nameKey] || '';

  const loadSuggestions = useCallback(async (query) => {
    if (!formSessionToken && !userProfile?.id) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const contacts = await fetchOpposingAgentContacts({
        side,
        query,
        formSessionToken,
        userId: userProfile?.id,
      });
      setSuggestions(contacts);
      setShowSuggestions(contacts.length > 0);
      setActiveSuggestion(-1);
    } catch (err) {
      console.error('Opposing agent autocomplete failed:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [formSessionToken, side, userProfile?.id]);

  useEffect(() => {
    loadSuggestions('');
  }, [loadSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNameChange = (value) => {
    setContractData((prev) => ({ ...prev, [nameKey]: value }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if ((value || '').trim().length >= 1) {
        loadSuggestions(value.trim());
      } else {
        loadSuggestions('');
      }
    }, 200);
    setShowSuggestions(true);
  };

  const applySuggestion = (contact) => {
    setContractData((prev) => ({
      ...prev,
      ...contactToFieldUpdates(side, contact),
    }));
    setShowSuggestions(false);
    setActiveSuggestion(-1);
  };

  const handleNameKeyDown = (event) => {
    if (!showSuggestions || !suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestion((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestion((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && activeSuggestion >= 0) {
      event.preventDefault();
      applySuggestion(suggestions[activeSuggestion]);
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="wizard-agent-info" ref={containerRef}>
      {AGENT_INFO_FIELDS.map(({ key, label, placeholder, inputType }) => {
        const fieldKey = fieldKeyForSide(side, key);
        const isNameField = key === 'name';
        const isPhoneField = inputType === 'phone';
        return (
          <div className={`wizard-input-group${isNameField ? ' wizard-agent-name-autocomplete' : ''}`} key={key}>
            <label>{label}</label>
            {isNameField ? (
              <>
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onFocus={() => {
                    if (suggestions.length) setShowSuggestions(true);
                  }}
                  onKeyDown={handleNameKeyDown}
                  placeholder={placeholder}
                  className="wizard-input"
                  autoComplete="off"
                />
                {loadingSuggestions && (
                  <div className="wizard-agent-suggest-status">Searching prior agents…</div>
                )}
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="wizard-agent-suggest-list" role="listbox">
                    {suggestions.map((contact, index) => (
                      <li key={`${contact.email || contact.name}-${index}`}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={index === activeSuggestion}
                          className={`wizard-agent-suggest-item${index === activeSuggestion ? ' active' : ''}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applySuggestion(contact)}
                        >
                          <span className="wizard-agent-suggest-name">{contact.name || 'Unknown agent'}</span>
                          {contact.brokerage && (
                            <span className="wizard-agent-suggest-meta">{contact.brokerage}</span>
                          )}
                          {contact.email && (
                            <span className="wizard-agent-suggest-meta">{contact.email}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : isPhoneField ? (
              <PhoneNumberInput
                value={contractData[fieldKey] || ''}
                onChange={(e) => setContractData((prev) => ({
                  ...prev,
                  [fieldKey]: e.target.value,
                }))}
                placeholder={placeholder}
                className="wizard-input"
              />
            ) : (
              <input
                type="text"
                value={contractData[fieldKey] || ''}
                onChange={(e) => setContractData((prev) => ({
                  ...prev,
                  [fieldKey]: e.target.value,
                }))}
                placeholder={placeholder}
                className="wizard-input"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OpposingAgentInfoFields;
